/**
 * Pinned pages and recently visited pages, and whether the navigation is
 * collapsed.
 *
 * Kept in THIS browser (localStorage), per administrator: a convenience for
 * moving around — back to the organization you were supporting a minute ago —
 * not platform data, so none of it touches the API. Losing it (a new browser,
 * cleared storage) costs nothing but a click.
 *
 * Each entry is { to, label, at }.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';

const RECENT_LIMIT = 8;
const PINNED_LIMIT = 12;
const EMPTY = Object.freeze({ pinned: [], recent: [] });

const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const keyFor = (scope) => `nexerp.console.workspace.${scope}`;

// A tiny read cache, so useSyncExternalStore sees the SAME object until the
// data really changes (it compares snapshots by identity).
const cache = new Map();

function read(scope) {
  if (!scope) return EMPTY;
  const raw = (() => {
    try {
      return localStorage.getItem(keyFor(scope));
    } catch {
      return null;
    }
  })();
  const hit = cache.get(scope);
  if (hit && hit.raw === raw) return hit.value;

  let value = EMPTY;
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    value = {
      pinned: Array.isArray(parsed?.pinned) ? parsed.pinned : [],
      recent: Array.isArray(parsed?.recent) ? parsed.recent : [],
    };
  } catch {
    value = EMPTY;
  }
  cache.set(scope, { raw, value });
  return value;
}

function write(scope, value) {
  if (!scope) return;
  try {
    localStorage.setItem(keyFor(scope), JSON.stringify(value));
  } catch {
    // Storage full or disabled: the preference simply is not kept.
  }
  notify();
}

const clean = (entry) => ({
  to: String(entry.to),
  label: String(entry.label ?? '').slice(0, 120),
  at: entry.at ?? Date.now(),
});

export function recordVisit(scope, entry) {
  if (!scope || !entry?.to || !entry?.label) return;
  const current = read(scope);
  const next = clean(entry);
  const existing = current.recent[0];
  // Re-rendering the same page must not rewrite storage on every render.
  if (existing && existing.to === next.to && existing.label === next.label) return;
  const recent = [next, ...current.recent.filter((item) => item.to !== next.to)].slice(0, RECENT_LIMIT);
  write(scope, { ...current, recent });
}

export function togglePinned(scope, entry) {
  if (!scope || !entry?.to) return;
  const current = read(scope);
  const isPinned = current.pinned.some((item) => item.to === entry.to);
  const pinned = isPinned
    ? current.pinned.filter((item) => item.to !== entry.to)
    : [clean(entry), ...current.pinned].slice(0, PINNED_LIMIT);
  write(scope, { ...current, pinned });
}

/** Reads and changes this administrator's pinned and recent pages. */
export function useWorkspacePrefs(scope) {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => read(scope),
    () => EMPTY
  );

  const isPinned = useCallback((to) => snapshot.pinned.some((item) => item.to === to), [snapshot]);

  return useMemo(
    () => ({
      pinned: snapshot.pinned,
      recent: snapshot.recent,
      isPinned,
      togglePinned: (entry) => togglePinned(scope, entry),
      recordVisit: (entry) => recordVisit(scope, entry),
    }),
    [snapshot, isPinned, scope]
  );
}

// --- the navigation's collapsed state ---------------------------------------

const NAV_KEY = 'nexerp.console.nav';

export function getNavCollapsed() {
  try {
    return localStorage.getItem(NAV_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

export function setNavCollapsed(collapsed) {
  try {
    if (collapsed) localStorage.setItem(NAV_KEY, 'collapsed');
    else localStorage.removeItem(NAV_KEY);
  } catch {
    /* ignored */
  }
  notify();
}

export function useNavCollapsed() {
  const collapsed = useSyncExternalStore(subscribe, getNavCollapsed, () => false);
  return [collapsed, setNavCollapsed];
}
